import { useEffect, useMemo, useRef, useState, useCallback } from "react";

/**
 * useFormDraft
 * - Guarda temporalmente el estado de un formulario en localStorage
 * - No guarda blobs/archivos (excluir con "exclude" o con un sanitizer)
 * - No guarda en modo edición
 */
export default function useFormDraft({
  formId,           // string único del formulario (p.ej. "informe-mantencion")
  userId,           // string único del usuario (p.ej. usuario?.id || usuario?.rut)
  modeId,           // string que diferencie creación/edición (p.ej. numeroOT || 'nuevo')
  watch,            // react-hook-form watch
  getValues,        // react-hook-form getValues
  reset,            // react-hook-form reset
  toast,            // ref a PrimeReact Toast (opcional)
  exclude = ["evidencias", "escaners", "firmas"],     // campos a excluir
  throttleMs = 800, // intervalo de guardado
  askOnMount = true, // preguntar al montar si existe borrador
  enabled = true  // habilitar/deshabilitar funcionalidad
}) {

  // Inicializar isOnline antes de cualquier early-return
  const initialOnline =
    typeof navigator !== "undefined" && "onLine" in navigator
      ? navigator.onLine : true;
  const [isOnline, setIsOnline] = useState(initialOnline);

  // Si está deshabilitado, storageKey será null y toda la lógica queda “apagada”
  const storageKey = useMemo(() => {
    if (!enabled) return null;
    const uid = userId ? String(userId) : "anon";
    const mid = modeId ? String(modeId) : "nuevo";
    return `draft:${formId}:${uid}:${mid}`;
  }, [enabled, formId, userId, modeId]);

  const [hasDraft, setHasDraft] = useState(false);
  const [restorePrompt, setRestorePrompt] = useState(false);

  // Sanea valores: elimina campos pesados/sensibles
  const sanitize = useCallback((data) => {
    const cloned = JSON.parse(JSON.stringify(data || {}));
    
    // excluir rutas ('evidencias', 'escaners', 'firmas')
    exclude.forEach((path) => {
      if (!path) return;
      const parts = path.split(".");
      let obj = cloned;
      for (let i = 0; i < parts.length - 1; i++) {
        obj = obj?.[parts[i]];
        if (!obj) break;
      }
      if (obj && parts.length) delete obj[parts[parts.length - 1]];
    });

    // Limpia firmas (si estan dentro de "firmas")
    if (cloned.firmas) cloned.firmas = { cliente: null, mecanico: null };
    // Limpia arrays de archivos (comunes)
    if (Array.isArray(cloned.evidencias)) cloned.evidencias = [];
    if (Array.isArray(cloned.escaners)) cloned.escaners = [];

    return cloned;
  }, [exclude]);

  // Cargar borrador
  const loadDraft = useCallback(() => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  // Guardar borrador
  const saveDraft = useCallback(() => {
    if (!enabled || !storageKey) return;
    try {
      const values = getValues();
      const cleaned = sanitize(values);
      const payload = { savedAt: new Date().toISOString(), values: cleaned };

      localStorage.setItem(storageKey, JSON.stringify(payload));
      setHasDraft(true);
    } catch (error) {
      console.warn("No se pudo guardar el borrador: ", error);
    }
  }, [enabled, getValues, sanitize, storageKey]);

  // Borrar borrador
  const clearDraft = useCallback(() => {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
    } catch {}
  }, [storageKey]);

  // Preguntar si hay borrador existente al montar
  useEffect(() => {
    if (!enabled || !askOnMount) return;
    const draft = loadDraft();

    const isMeaningful = (obj) => {
      if (!obj || typeof obj !== 'object') return false;
      // Considera significativo si hay al menos un valor no vacío
      return Object.values(obj).some(v => {
        if (Array.isArray(v)) return v.length > 0;
        if (v && typeof v === 'object') return Object.keys(v).length > 0;
        return v !== '' && v !== null && v !== undefined;
      });
    };

    if (draft?.values && isMeaningful(draft.values)) {
      setHasDraft(true);
      setRestorePrompt(true);
    } 
  }, [enabled, askOnMount, loadDraft]);

  // Restaurar
  const restore = useCallback(() => {
    if (!enabled) return;
    const draft = loadDraft();
    if (draft?.values) {
      reset(draft.values);

      try {
        saveDraft(); // guardar inmediatamente para actualizar timestamp
      } catch (error) {
        console.warn("No se pudo guardar el borrador al restaurar: ", error);
      }

      toast?.current?.show?.({
        severity: 'info',
        summary: 'Borrador restaurado',
        detail: 'Continuas desde donde lo dejaste.',
        life: 2500
      });
      setRestorePrompt(false);
    }
  }, [enabled, loadDraft, reset, saveDraft, toast]);

  // Empezar de cero (descartar)
  const discard = useCallback(() => {
    if (!enabled) return;
    clearDraft();
    setRestorePrompt(false);
    toast?.current?.show?.({
      severity: 'warn',
      summary: 'Borrador descartado',
      detail: 'Comenzaste un registro nuevo.',
      life: 2000
    });
  }, [enabled, clearDraft, toast]);

  // Autoguardado con throttle (escucha cambios de react-hook-form)
  const saveTimeout = useRef(null);
  useEffect(() => {
    if (!enabled) return;

    const subscription = watch(() => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      const delay = isOnline ? throttleMs : Math.min(200, throttleMs);
      saveTimeout.current = setTimeout(() => {
        saveDraft();
      }, delay);
    });

    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      subscription?.unsubscribe?.();
    };
  }, [enabled, watch, saveDraft, throttleMs, isOnline]);

  // Online/Offline: guardar inmediatamente al quedar offline
  useEffect(() => {
    if (!enabled) return;

    const onOnline = () => {
      setIsOnline(true);
      // Toast online
      toast?.current?.show?.({
        severity: "success",
        summary: "Conexión restaurada",
        detail: "Tu conexión ha vuelto. Los cambios se seguirán guardando normalmente.",
        life: 3500
      });
    };

    const onOffline = () => {
      setIsOnline(false);
      // Guardado inmediato
      try {
        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveDraft();
      } catch { }
      // Toast offline
      toast?.current?.show?.({
        severity: "warn",
        summary: "Sin conexión",
        detail: "Estás trabajando sin internet. El progreso se guardará localmente.",
        life: 5000
      });
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [enabled, saveDraft, toast]);

  // Guardar al ocultar/cerrar la pestaña (robusto en móviles/desktop)
  useEffect(() => {
    if (!enabled) return;

    const flushSave = () => {
      try {
        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveDraft();
      } catch {}
    };

    const onVisibilityChange = () => {
      if (document.hidden) flushSave();
    };
    const onPageHide = () => flushSave();
    const onBeforeUnload = () => flushSave();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [enabled, saveDraft]);

  return {
    storageKey,
    hasDraft,
    isOnline,
    restorePrompt,
    restore,
    discard,
    saveDraft,
    clearDraft
  };
}